// Align client-side models to server models (api/core/models.ts)
import type { AccessGovernanceMode, Contact, Site, List, Subscription, TenantConfiguration, Label, CustomFieldDefinition, TenantAdminSource } from "@xobcl/types";
import { config } from './config';
import { logger } from './logger';
import type { PlatformStartPage, PublicStartPage, StartPageBlock, StartPageImageUpload } from '../startPage/types';

export type { Contact, Site, List, Subscription, TenantConfiguration, Label, CustomFieldDefinition };
export type QuickBtn = Site['quickDial'][number];

export type Status = {
  ok: boolean;
  service: string;
  version: string;
  timestamp: string;
};

export type FeatureFlags = {
  authentication: boolean;
  userProfiles: boolean;
  contactManagement: boolean;
  teamsPresence: boolean;
  profilePhotos: boolean;
  userSearch: boolean;
  outlookSync: boolean;
  contactFolders: boolean;
  configuredCredentials: string[];
  missingCredentials: string[];
};

export const FEATURES_FAIL_CLOSED: FeatureFlags = {
  authentication: false,
  userProfiles: false,
  contactManagement: true,
  teamsPresence: false,
  profilePhotos: false,
  userSearch: false,
  outlookSync: false,
  contactFolders: false,
  configuredCredentials: [],
  missingCredentials: ['client', 'presence', 'contact-sync'],
};

export type Me = {
  tenantId: string;
  userId: string;
  upn?: string;
  name?: string;
  roles: string[]; // Legacy Azure AD roles
  tenantRole?: 'TenantAdmin' | 'Manager' | 'User' | 'Guest'; // New multi-tenant role
  homeSiteId: string | null;
  usageCountry: string | null;
  quickDialAsStartPage?: "tenant-default" | "on" | "off";
  language?: string | null;
  favoriteContactIds?: string[];
  /**
   * Per-list contact-list column layouts chosen by the user. Keyed by list id,
   * plus the reserved "__default__" key for the user's all-lists default; the
   * value is the ordered list of column keys (built-in field keys or
   * "custom:<fieldId>").
   */
  listColumnPrefs?: Record<string, string[]>;
  /**
   * True when the first-run onboarding guide should be shown to this user. Set by the
   * backend for tenant admins until any admin completes/dismisses onboarding via
   * {@link completeOnboarding}.
   */
  showOnboarding?: boolean;
  /** Where the caller's effective tenant-admin status comes from. Never implies HostingPortal access. */
  effectiveAdminSource?: TenantAdminSource;
  /** The tenant's access-governance mode. */
  accessGovernanceMode?: AccessGovernanceMode;
  /** Raw, server-validated Entra token roles. Only the HostingPortal guard may read these. */
  entraRoles?: string[];
  /**
   * When the tenant's trial ends, ISO-8601, or null when no trial is in force. Sent to every user;
   * the app-wide trial banner decides who sees it when.
   */
  trialEndsAt?: string | null;
  /**
   * Premium features the tenant's subscription includes (see {@link PRODUCT_FEATURES}). Display
   * only: every premium endpoint checks the entitlement again server-side.
   */
  productFeatures?: string[];
  // Permission flags for easy client-side checking
  permissions?: {
    canManageUsers: boolean;
    canCreateSites: boolean;
    canCreateLists: boolean;
    canViewAllContacts: boolean;
  };
};

// Optional auth header provider (wired by app using useApiToken)
let authHeaderProvider: null | (() => Promise<string>) = null;
export function setAuthHeaderProvider(fn: () => Promise<string>) {
  authHeaderProvider = fn;
}

// API Base URL configuration from environment variables
const API_BASE_URL = config.api.baseUrl;

// **PERFORMANCE: Simple in-memory cache for sites data (5 minute TTL)**
type CacheEntry<T> = { data: T; timestamp: number };
const cache: { sites?: CacheEntry<Site[]>; me?: CacheEntry<Me>; features?: CacheEntry<FeatureFlags> } = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCached<T>(key: 'sites' | 'me' | 'features'): T | null {
  const entry = cache[key];
  if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
    return entry.data as T;
  }
  return null;
}

function setCache<T>(key: 'sites' | 'me' | 'features', data: T): void {
  cache[key] = { data, timestamp: Date.now() } as any;
}

export function clearCache(): void {
  delete cache.sites;
  delete cache.me;
  delete cache.features;
}

/** Error carrying the HTTP status so callers can branch on specific codes (e.g. 409 conflict). */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * The backend returns HTTP 403 with error code `consent_required` from directory-search endpoints when
 * QuickDial has not been admin-consented in the caller's own tenant, so no app-only Graph token can be
 * issued there. Detecting this lets the UI prompt "ask your admin to enable QuickDial" instead of showing
 * a generic failure. See GraphIntegrationFunctions.HandleConsentRequiredAsync on the API side.
 */
export function isConsentRequiredError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && error.message === 'consent_required';
}

async function apiFetch<T>(path: string, options: RequestInit = {}, silentOn?: number[], allowNoContent = false): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> | undefined),
  };
  
  // The API authenticates only the Entra bearer token below; it calls the Function App directly,
  // so no SWA-injected identity header exists (and the API would ignore one).

  // Wait a bit for auth provider to be set up if not available yet
  let attempts = 0;
  while (!authHeaderProvider && attempts < 10) {
    await new Promise(resolve => setTimeout(resolve, 100));
    attempts++;
  }
  
  if (authHeaderProvider) {
    try {
      const token = await authHeaderProvider();
      logger.debug('Token acquired for API call', { hasToken: !!token }, 'API');
      if (token) headers["Authorization"] = `Bearer ${token}`;
    } catch (err) {
      logger.error('Token acquisition failed', err, 'API');
      // ignore token fetch errors; let API return 401 which UI can handle
    }
  } else {
    logger.warn('No authHeaderProvider set after waiting', undefined, 'API');
  }
  
  // All Azure Functions need /api prefix
  const apiPath = `/api${path}`;
  const url = `${API_BASE_URL}${apiPath}`;
  logger.apiCall(options.method || 'GET', url, options.body ? JSON.parse(options.body as string) : undefined);
  
  const res = await fetch(url, { 
    ...options, 
    headers,
    credentials: 'include' // Important: This ensures SWA authentication headers are sent
  });
  
  logger.apiResponse(res.status, res.statusText, url);
  if (!res.ok) {
    // Try to parse error details from response body
    let errorMessage = `API error ${res.status}`;
    try {
      const errorData = await res.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // If response is not JSON, use status text
      errorMessage = `API error ${res.status}: ${res.statusText}`;
    }
    
    // Only log error if not in silent status codes list
    if (!silentOn || !silentOn.includes(res.status)) {
      logger.apiError(url, errorMessage);
    }

    throw new ApiError(res.status, errorMessage);
  }
  if (res.status === 202 || res.status === 204) {
    if (allowNoContent) {
      return undefined as T;
    }

    throw new ApiError(res.status, `API error ${res.status}: Empty response body`);
  }

  return await res.json() as T;
}

// ---------- Contacts ----------
export async function getContacts(): Promise<Contact[]> {
  return apiFetch<Contact[]>("/contacts");
}
// DTO mappers: accept thin input and map to server fields where needed
export type ContactInput = Partial<Pick<Contact, "firstName"|"lastName"|"email"|"mobile"|"phone"|"teamsUserId"|"entraObjectId"|"listIds"|"labelIds"|"notes"|"order"|"kind"|"avatarColor"|"title"|"department"|"company"|"employeeId"|"employeeType"|"location"|"street"|"city"|"postalCode"|"country"|"customValues">>;
export type SiteInput = Partial<Pick<Site, "name"|"mode"|"lat"|"lon"|"radiusKm"|"countryCode"|"quickDialEnabled"|"quickDial"|"contactListId"|"mobileBackgroundId"|"managers"|"viewers">>;

export async function upsertContact(c: ContactInput): Promise<Contact> {
  const payload = { type: "contact", listIds: [], ...c };
  return apiFetch<Contact>("/contacts", { method: "POST", body: JSON.stringify(payload) });
}

export async function deleteContact(id: string): Promise<void> {
  // Use the same auth header provider as apiFetch
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (authHeaderProvider) {
    try {
      const token = await authHeaderProvider();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    } catch { /* ignore */ }
  }
  const url = `${API_BASE_URL}/api/contacts?id=${encodeURIComponent(id)}`;
  const res = await fetch(url, { method: "DELETE", headers });
  if (!res.ok) throw new Error(`API error ${res.status}`);
}

// ---------- Sites ----------
export async function getSites(): Promise<Site[]> {
  // Check cache first for instant load on return visits
  const cached = getCached<Site[]>('sites');
  if (cached) {
    logger.debug('Using cached sites data', undefined, 'API');
    return cached;
  }
  
  const sites = await apiFetch<Site[]>("/sites");
  setCache('sites', sites);
  return sites;
}
export async function upsertSite(s: SiteInput): Promise<Site> {
  const payload = { type: "site", quickDialEnabled: true, quickDial: [], ...s };
  const result = await apiFetch<Site>("/sites", { method: "POST", body: JSON.stringify(payload) });
  delete cache.sites; // Invalidate cache when site is updated
  return result;
}

export async function deleteSite(id: string): Promise<void> {
  // Use the same auth header provider as apiFetch
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (authHeaderProvider) {
    try {
      const token = await authHeaderProvider();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    } catch { /* ignore */ }
  }
  const url = `${API_BASE_URL}/api/sites?id=${encodeURIComponent(id)}`;
  const res = await fetch(url, { method: "DELETE", headers });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  delete cache.sites; // Invalidate cache when site is deleted
}

// ---------- Lists ----------
export async function getLists(): Promise<List[]> {
  return apiFetch<List[]>("/lists");
}

export async function upsertList(l: Partial<List>): Promise<List> {
  return apiFetch<List>("/lists", { method: "POST", body: JSON.stringify({ type: "list", ...l }) });
}

export async function deleteList(id: string): Promise<void> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (authHeaderProvider) {
    try { const t = await authHeaderProvider(); if (t) headers["Authorization"] = `Bearer ${t}`; } catch {}
  }
  const url = `${API_BASE_URL}/api/lists?id=${encodeURIComponent(id)}`;
  const res = await fetch(url, { method: "DELETE", headers });
  if (!res.ok) throw new Error(`API error ${res.status}`);
}

// ---------- Labels ----------
export async function getLabels(listId?: string): Promise<Label[]> {
  const path = listId ? `/labels?listId=${encodeURIComponent(listId)}` : "/labels";
  return apiFetch<Label[]>(path);
}

export async function upsertLabel(l: Partial<Label>): Promise<Label> {
  return apiFetch<Label>("/labels", { method: "POST", body: JSON.stringify({ type: "label", ...l }) });
}

export async function deleteLabel(id: string): Promise<{ success: boolean; removedFromContactCount: number }> {
  return apiFetch<{ success: boolean; removedFromContactCount: number }>(
    `/labels/${encodeURIComponent(id)}`,
    { method: "DELETE" }
  );
}

// ---------- Subscriptions ----------
export async function getSubscriptions(listId: string): Promise<Subscription[]> {
  return apiFetch<Subscription[]>(`/subscriptions?listId=${encodeURIComponent(listId)}`);
}

export async function addSubscription(input: { listId: string; userId: string; role: "viewer"|"subscriber" }): Promise<Subscription> {
  return apiFetch<Subscription>(`/subscriptions`, { method: "POST", body: JSON.stringify({ type: "subscription", ...input }) });
}

export async function deleteSubscription(id: string): Promise<void> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (authHeaderProvider) {
    try { const t = await authHeaderProvider(); if (t) headers["Authorization"] = `Bearer ${t}`; } catch {}
  }
  const url = `${API_BASE_URL}/api/subscriptions?id=${encodeURIComponent(id)}`;
  const res = await fetch(url, { method: "DELETE", headers });
  if (!res.ok) throw new Error(`API error ${res.status}`);
}

// ---------- Group subscribers ----------
export interface GroupSubscriberMember {
  userId: string;
  upn: string | null;
  name: string | null;
}

export interface GroupSubscriberDetail {
  groupId: string;
  displayName: string | null;
  mail: string | null;
  lastMembersSyncAt: string | null;
  members: GroupSubscriberMember[];
}

/** Returns the cached membership snapshot for an Entra group that subscribes to a list. */
export async function getGroupSubscriber(groupId: string): Promise<GroupSubscriberDetail> {
  return apiFetch<GroupSubscriberDetail>(`/group-subscribers/${encodeURIComponent(groupId)}`);
}

// ---------- Status ----------
export async function getStatus(): Promise<Status> {
  return apiFetch<Status>("/status");
}

// ---------- Me ----------
export const ME_REFRESH_EVENT = 'quickdial:me-refresh';

export async function getMe(forceRefresh: boolean = false): Promise<Me> {
  if (forceRefresh) {
    delete cache.me;
  }

  // Check cache first for instant load
  const cached = forceRefresh ? undefined : getCached<Me>('me');
  if (cached) {
    // MIGRATION: Invalidate cache if old boolean format detected
    const quickDialValue = (cached as any).quickDialAsStartPage;
    if (typeof quickDialValue === 'boolean') {
      logger.debug('Invalidating cached user data due to format migration', undefined, 'API');
      delete cache.me;
    } else {
      logger.debug('Using cached user data', undefined, 'API');
      return cached;
    }
  }
  
  const me = await apiFetch<Me>("/me");
  setCache('me', me);
  return me;
}

/**
 * Marks the first-run onboarding guide as completed for the caller's tenant (tenant-admin only).
 * Invalidates the cached `me` so a subsequent {@link getMe} reflects `showOnboarding: false`.
 */
export async function completeOnboarding(): Promise<{ onboardingCompletedAt: string | null }> {
  const result = await apiFetch<{ onboardingCompletedAt: string | null }>("/onboarding/complete", {
    method: "POST"
  });
  delete cache.me;
  return result;
}

// ---------- Features ----------
export async function getFeatures(forceRefresh: boolean = false): Promise<FeatureFlags> {
  if (!forceRefresh) {
    const cached = getCached<FeatureFlags>('features');
    if (cached) {
      logger.debug('Using cached features data', undefined, 'API');
      return cached;
    }
  } else {
    // Clear cache entry to force fresh fetch
    delete cache.features;
    logger.debug('Force refreshing features data', undefined, 'API');
  }

  const features = await apiFetch<FeatureFlags>("/features");
  setCache('features', features);
  return features;
}

// ---------- User preferences ----------
export async function setHomeSite(homeSiteId: string | null): Promise<{ ok: boolean; homeSiteId: string | null }> {
  const result = await apiFetch<{ ok: boolean; homeSiteId: string | null }>("/userprefs", {
    method: "PATCH",
    body: JSON.stringify({ homeSiteId })
  });
  delete cache.me; // Invalidate user cache when preferences change
  return result;
}

export async function setUserPreferences(prefs: { homeSiteId?: string | null; quickDialAsStartPage?: "tenant-default" | "on" | "off"; language?: string | null; favoriteContactIds?: string[]; listColumnPrefs?: Record<string, string[]> }): Promise<{ ok: boolean; homeSiteId: string | null; quickDialAsStartPage: "tenant-default" | "on" | "off"; language: string | null; favoriteContactIds: string[]; listColumnPrefs: Record<string, string[]> }> {
  const result = await apiFetch<{ ok: boolean; homeSiteId: string | null; quickDialAsStartPage: "tenant-default" | "on" | "off"; language: string | null; favoriteContactIds: string[]; listColumnPrefs: Record<string, string[]> }>("/userprefs", {
    method: "PATCH",
    body: JSON.stringify(prefs)
  });
  delete cache.me; // Invalidate user cache when preferences change
  return result;
}

// ---------- Export/Import ----------
export async function exportContactsHTML(listId?: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (authHeaderProvider) {
    try {
      const token = await authHeaderProvider();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    } catch { /* ignore */ }
  }

  const query = listId ? `?listId=${encodeURIComponent(listId)}` : '';
  const url = `${API_BASE_URL}/api/export-import/contacts/html${query}`;
  const res = await fetch(url, {
    headers,
    credentials: 'include'
  });

  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.blob();
}

export async function exportContactsCSV(listId?: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (authHeaderProvider) {
    try {
      const token = await authHeaderProvider();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    } catch { /* ignore */ }
  }

  const query = listId ? `?listId=${encodeURIComponent(listId)}` : '';
  const url = `${API_BASE_URL}/api/export-import/contacts/csv${query}`;
  const res = await fetch(url, {
    headers,
    credentials: 'include'
  });

  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.blob();
}

export type ImportResult = {
  success: number;
  errors: Array<{ row: number; error: string; data?: any }>;
  total: number;
};

export async function importContactsCSV(csvData: string, targetListId?: string): Promise<ImportResult> {
  return apiFetch<ImportResult>("/export-import/contacts/import", {
    method: "POST",
    body: JSON.stringify({ csvData, targetListId })
  });
}

export type PortalTenantSettings = {
  displayName?: string;
  allowSelfSubscribe?: boolean;
  siteAwareDefault?: boolean;
  enableContactSync?: boolean;
  contactSyncSuffix?: string;
};

export type PortalTenantAdminUser = {
  userId: string;
  name?: string;
  upn?: string;
  roles: string[];
};

export type PortalTenantSummary = {
  tenantId: string;
  tenantName: string;
  status: string;
  userCount: number;
  contactCount: number;
  listCount: number;
  siteCount: number;
  subscriptionCount: number;
  subscriptionTier: string;
  createdDate: string;
  lastActivity: string;
  settings: PortalTenantSettings;
  adminUsers: PortalTenantAdminUser[];
  /** The built-in package on the tenant document. Under AppLauncher this is rollback input only. */
  selectedPackageId?: string | null;
  /** How licensing resolves for this tenant right now — the source to display, not `subscriptionTier`. */
  licensing?: TenantLicenseProviderState;
};

export type PortalTenantUser = {
  id: string;
  userId: string;
  upn?: string;
  name?: string;
  roles: string[];
  createdAt: string;
  updatedAt: string;
};

export type PortalTenantContact = {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  createdAt: string;
  updatedAt: string;
};

export type PortalTenantList = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

export type PortalTenantSite = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

export type PortalTenantDetail = PortalTenantSummary & {
  users: PortalTenantUser[];
  recentContacts: PortalTenantContact[];
  recentLists: PortalTenantList[];
  recentSites: PortalTenantSite[];
  license?: TenantLicenseConfiguration;
};

export type PortalTenantInventoryResponse = {
  tenants: PortalTenantSummary[];
  totalCount: number;
  timestamp: string;
};

export type LicensePackageInfo = {
  packageId: string;
  name: string;
  description?: string;
  sku: string;
  pricePerUnit: number;
  currency: string;
  minimumQuantity: number;
  maximumQuantity: number;
  isActive: boolean;
  features: string[];
  retiredAt?: string;
  maxSites: number;
  maxLists: number;
  maxSyncUsers: number;
  maxViewingUsers: number;
  maxContacts: number;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
};

/** The platform-wide licensing mode. Set only from the hosting portal, and it applies to every tenant. */
export type LicensingProvider = 'built-in' | 'app-launcher';

/**
 * What is actually authoritative right now. `unavailable` means the platform runs on AppLauncher but
 * no entitlement could be resolved for this tenant — it never means the tenant fell back to
 * built-in licensing.
 */
export type EffectiveLicensingProvider = LicensingProvider | 'unavailable';

export type LicenseAvailabilityReason =
  | 'globally-disabled'
  | 'misconfigured'
  | 'not-registered'
  /**
   * AppLauncher knows QuickDial but holds no customer link for this tenant — where every
   * organization sits until it buys something, since the purchase is what creates the link. Distinct
   * from `not-registered`, which is the rest of the same 404 class and is a registration fault only
   * an operator can clear.
   */
  | 'not-onboarded'
  | 'authentication-failed'
  | 'upstream-unavailable'
  | 'no-valid-cache'
  | 'unsupported-provider';

export type LicenseAccessMode = 'full' | 'warning' | 'readOnly' | 'blocked';

/**
 * Backend-resolved provider state for one tenant. Since the provider is chosen platform-wide, this is
 * per-tenant integration *health*, not per-tenant intent. Status metadata only — never credentials or
 * upstream bodies.
 */
export type TenantLicenseProviderState = {
  /** The platform licensing mode in force. */
  configuredProvider: LicensingProvider;
  effectiveProvider: EffectiveLicensingProvider;
  appLauncherAvailable: boolean;
  availabilityReason?: LicenseAvailabilityReason | null;
  accessMode: LicenseAccessMode;
  isStale: boolean;
  lastSuccessfulRefresh?: string | null;
  cacheExpiresAt?: string | null;
  /** Limits actually enforced now: the entitlement under AppLauncher, the package under built-in. */
  enforcedLimits?: ResolvedResourceLimits | null;
  planCode?: string | null;
  /** The plan's published catalog name, or null when the catalog does not list it. */
  planDisplayName?: string | null;
  /** AppLauncher's lifecycle status, verbatim. Display only. */
  subscriptionStatus?: string | null;
  isCancelled?: boolean;
  isLocalTrial?: boolean;
  validTo?: string | null;
  cancelAtPeriodEnd?: boolean;
  trialEnd?: string | null;
};

export type TenantLicenseConfiguration = {
  tenantId: string;
  assignedPackageIds: string[];
  selectedPackageId?: string;
  selectedPackage?: LicensePackageInfo;
  maxSites?: number;
  maxLists?: number;
  maxSyncUsers?: number;
  maxViewingUsers?: number;
  effectiveMaxSites: number;
  effectiveMaxLists: number;
  effectiveMaxSyncUsers?: number;
  effectiveMaxViewingUsers?: number;
  updatedAt: string;
  provider: TenantLicenseProviderState;
};

export type UpdateTenantLicensePayload = {
  assignedPackageIds: string[];
  selectedPackageId?: string | null | undefined;
  maxSites?: number | null | undefined;
  maxLists?: number | null | undefined;
  maxSyncUsers?: number | null | undefined;
  maxViewingUsers?: number | null | undefined;
};

/** AppLauncher key contract this deployment is configured with. Read-only. */
export type PlatformLicensingMapping = {
  coreFeatureKey: string;
  /** AppLauncher feature key that unlocks organization directory search (premium). */
  directorySearchFeatureKey: string;
  maxSites: string;
  maxLists: string;
  maxSyncUsers: string;
  maxViewingUsers: string;
  /**
   * True when these keys come from a stored portal override rather than the deployment
   * configuration. Absent on request bodies, which never carry it.
   */
  isOverridden?: boolean;
};

/**
 * Platform-wide licensing settings. `mode` is the hosting admin's choice; `appLauncherAvailable`
 * is whether the deployment can honor it. Activating the mode while it is false puts every tenant
 * into `unavailable`, so both are shown together.
 */
export type PlatformLicensingSettings = {
  mode: LicensingProvider;
  appLauncherAvailable: boolean;
  availabilityReason?: LicenseAvailabilityReason | null;
  /** Detail behind an unusable key mapping; null unless the mapping itself is the problem. */
  mappingError?: string | null;
  appCode?: string | null;
  portalUrl?: string | null;
  mapping: PlatformLicensingMapping;
  /** The deployment-configured keys, so the editor can offer "reset to configured". */
  configuredMapping: PlatformLicensingMapping;
  updatedAt?: string | null;
  updatedBy?: string | null;
  /** When the key mapping was last overridden from the portal; null while it never has been. */
  mappingUpdatedAt?: string | null;
  mappingUpdatedBy?: string | null;
};


/**
 * The calling tenant admin's own licensing state. Distinct from {@link PlatformLicensingSettings},
 * which is hosting-admin-only: this is the tenant-scoped view that decides whether the Admin page
 * offers the built-in order form or the AppLauncher purchase path.
 */
export type TenantLicensingOverview = {
  mode: LicensingProvider;
  effectiveProvider: EffectiveLicensingProvider;
  accessMode: LicenseAccessMode;
  availabilityReason?: LicenseAvailabilityReason | null;
  isStale: boolean;
  limits: ResolvedResourceLimits;
  /**
   * Where to manage the subscription. Null under built-in licensing, and also null when AppLauncher
   * is active but no portal URL is configured — render no link rather than a dead one.
   */
  purchaseUrl?: string | null;
  /** False under AppLauncher, where the built-in order form must not be offered. */
  builtInOrdersEnabled: boolean;
  /**
   * The AppLauncher plan named on this tenant's entitlement, used to mark the matching card. Null
   * under built-in licensing and whenever AppLauncher named no plan — show no marker rather than
   * guessing.
   *
   * Naming a plan is not the same as being on one: a cancelled subscription keeps naming the plan it
   * was cancelled from. Read `holdsActivePlan` before treating this as the tenant's current plan,
   * before offering a plan change against it, and before withholding a purchase because of it.
   */
  currentPlanCode?: string | null;
  /**
   * Whether `currentPlanCode` is a plan the tenant holds now rather than one it used to hold. False
   * under built-in licensing, whenever no plan is named, and for a cancelled subscription. Decided by
   * the API so the SPA cannot combine the plan code with the lifecycle status differently than the
   * catalog projection did — the plan cards carry their signup links exactly when this is false.
   */
  holdsActivePlan?: boolean;
  /**
   * The published plan catalog, ordered cheapest-first. Empty under built-in licensing, and also
   * empty when the catalog could not be read — pricing is display material, so render no cards
   * rather than an error.
   */
  catalog: LicensingPlanOption[];
  /**
   * Add-ons published against the plan this tenant currently holds. Empty whenever there is nothing
   * to offer — built-in licensing, no subscription, or a plan that sells no extras — so rendering
   * the section whenever this is non-empty can never offer an add-on the subscription cannot take.
   */
  addOns: LicensingPlanAddOn[];
  /**
   * AppLauncher's own identifier for the cause (`tenant_not_linked`, `tenant_not_allowed`, …),
   * passed through verbatim. Render it only as a technical detail beside the explanation
   * `availabilityReason` selects — it is the string a tenant admin can quote in a support request,
   * and the set is open upstream, so never map an unrecognised value to "unknown".
   */
  upstreamReason?: string | null;
  /**
   * Whether a trial may be offered to this tenant: published by the operator, and withheld once a
   * subscription has been cancelled, since the trial belongs to the customer and a returning one has
   * already had theirs. Only picks the wording of a plan card's button — the trial itself is a
   * property of the price AppLauncher bills, so promising one it would refuse is what this prevents.
   */
  allowTrial?: boolean;
  /**
   * AppLauncher's lifecycle status, verbatim. The vocabulary is open and dual-spelled upstream
   * (`canceled` from Stripe, `cancelled` from their state machine), so never compare this against a
   * literal — `isCancelled` is the decided form. Null under built-in licensing.
   */
  subscriptionStatus?: string | null;
  /**
   * The subscription was ended, as opposed to never having existed. Both are `blocked`, so this
   * changes no access — only whether a customer who just cancelled is addressed as one.
   */
  isCancelled?: boolean;
  /**
   * End of the paid period: the renewal date, or the day access stops when `cancelAtPeriodEnd` is
   * set. ISO-8601. Null under built-in licensing and when AppLauncher named no period.
   */
  validTo?: string | null;
  /**
   * The subscription ends at `validTo` instead of renewing. This is what a customer's own
   * cancellation produces, since AppLauncher does not offer them an immediate one.
   */
  cancelAtPeriodEnd?: boolean;
  /** When the trial ends, ISO-8601, or null when not trialing. */
  trialEnd?: string | null;
  /** The plan taking over at `pendingPlanEffectiveAt`, or null when the plan simply renews. */
  pendingPlanCode?: string | null;
  /** When `pendingPlanCode` takes over, ISO-8601, or null. */
  pendingPlanEffectiveAt?: string | null;
  /**
   * AppLauncher's advisories for this tenant, already filtered to the ones addressed to the
   * subscribing organization. Messages are AppLauncher's own text and are deliberately not
   * translated — see the API contract for why — so render them attributed rather than as QuickDial's
   * own copy.
   */
  warnings?: TenantLicensingWarning[];
  /**
   * What the tenant has right now, in the catalog's own rows (same labels, same order) with values
   * from the entitlement. Lets a plan that is not in the catalog — AppLauncher's own trial — sit
   * beside the published cards. -1 means unlimited. Empty when no rights are held.
   */
  currentPlanLimits?: LicensingPlanLimit[];
};

/** One AppLauncher advisory, projected for display. */
export type TenantLicensingWarning = {
  /** Stable identifier, e.g. `PAYMENT_PAST_DUE`. The set is open upstream. */
  code: string;
  /** Normalized by the API to exactly these three. */
  severity: 'info' | 'warning' | 'error';
  /** AppLauncher's own wording. */
  message: string;
};

/** One AppLauncher plan card, already flattened for display by the API. */
export type LicensingPlanOption = {
  planCode: string;
  displayName: string;
  description?: string | null;
  /** Display price excluding VAT, in minor units. Null when free, unpriced, or withheld. */
  amountExVatMinor?: number | null;
  currency?: string | null;
  billingInterval?: 'monthly' | 'yearly' | 'oneTime' | string | null;
  /** What one unit is when the plan is sold per unit, else null. */
  billingUnit?: string | null;
  trialDays?: number | null;
  /** The amount is only published to signed-in viewers; render "sign in for pricing", not "free". */
  signInForPricing: boolean;
  features: string[];
  limits: LicensingPlanLimit[];
  /** The immutable price version behind the amount, or null when no price resolved. */
  priceVersionId?: string | null;
  /**
   * Deep link into AppLauncher's signup wizard for this exact price, or null when there is no such
   * offer to make — no portal address, no app code, a withheld price, or a tenant that already
   * subscribes and must change plan rather than start one. The API withholds it in every one of
   * those cases, so a button rendered whenever this is set can never lead nowhere.
   */
  signupUrl?: string | null;
};

/** One add-on a subscribed tenant can buy on top of the plan they hold. */
export type LicensingPlanAddOn = {
  planCode: string;
  displayName: string;
  description?: string | null;
  /** Display price excluding VAT, in minor units. Null when no price resolved. */
  amountExVatMinor?: number | null;
  currency?: string | null;
  billingInterval?: 'monthly' | 'yearly' | 'oneTime' | string | null;
  /** What one unit is when the add-on is sold per unit, else null. */
  billingUnit?: string | null;
  features: string[];
  /**
   * The capacity *one unit* of this add-on grants — an increment, never a total. Render it beside
   * the plan's own limits rather than adding it into them.
   */
  limits: LicensingPlanLimit[];
};

/** One curated limit row on a plan card. */
export type LicensingPlanLimit = {
  displayName: string;
  /** The cap. Minor units when {@link valueKind} is `money`. */
  value: number;
  valueKind?: 'count' | 'money' | string | null;
  currency?: string | null;
  unitSuffix?: string | null;
  /** AppLauncher's metric key for the row — the key {@link TenantLicensingOverview.currentPlanLimits} lines up on. */
  metric?: string | null;
};

/** Limits as the resolver normalizes them; null means unlimited. */
export type ResolvedResourceLimits = {
  maxSites: number;
  maxLists?: number | null;
  maxSyncUsers?: number | null;
  maxViewingUsers?: number | null;
  maxContacts?: number | null;
  maxUsers?: number | null;
};

/** One tenant's answer to "what would AppLauncher say about you right now". */
export type LicensingReconciliationRow = {
  tenantId: string;
  organizationName?: string | null;
  isActive: boolean;
  selectedPackageId?: string | null;
  subscriptionTier: string;
  effectiveProvider: EffectiveLicensingProvider;
  accessMode: LicenseAccessMode;
  availabilityReason?: LicenseAvailabilityReason | null;
  /** True when the tenant would keep writing exactly as it does today. */
  wouldRetainWriteAccess: boolean;
  limits?: ResolvedResourceLimits | null;
  isStale: boolean;
  /** Set when the probe itself failed, as distinct from AppLauncher answering "blocked". */
  probeError?: string | null;
};

/**
 * The pre-flip dry run. An unsubscribed tenant resolves as a perfectly valid `blocked` entitlement,
 * so no kill switch and no fall-back rule catches it — this report is the only thing that does.
 */
export type LicensingReconciliationReport = {
  generatedAt: string;
  /** The mode in force while the report was produced. Expected to be built-in. */
  currentMode: LicensingProvider;
  appCode?: string | null;
  tenantCount: number;
  wouldRetainWriteAccessCount: number;
  wouldBecomeReadOnlyCount: number;
  wouldBecomeBlockedCount: number;
  unavailableCount: number;
  probeErrorCount: number;
  /** True only when every active tenant would keep write access. */
  isClearToFlip: boolean;
  tenants: LicensingReconciliationRow[];
};

/** Outcome of one AppLauncher self-test step. */
export type AppLauncherCheckOutcome = 'passed' | 'failed' | 'warning' | 'skipped';

/** Stable ids for the self-test steps; the UI translates from these rather than from prose. */
export type AppLauncherCheckId =
  | 'configuration'
  | 'token-acquisition'
  | 'caller-identity'
  | 'reachability'
  | 'entitlement';

export type AppLauncherCheck = {
  id: AppLauncherCheckId;
  outcome: AppLauncherCheckOutcome;
  /** Machine-readable detail code, never a raw upstream body or exception message. */
  detail?: string | null;
};

/** One row of AppLauncher's allowed-tenants list. */
export type AppLauncherAllowedTenant = {
  tenantId: string;
  organizationName?: string | null;
  isActive: boolean;
};

/**
 * The live self-test result plus everything the AppLauncher operator needs to register QuickDial.
 * `callerClientId`, `appCode` and `allowedTenants` are the three fields AppLauncher's own form asks
 * for; the rest explains what happened when QuickDial last tried to call it.
 */
export type AppLauncherConnectivityReport = {
  checkedAt: string;
  /** True only when every step passed; warnings do not clear it. */
  isHealthy: boolean;
  checks: AppLauncherCheck[];
  /** Read from the token QuickDial actually received, not from configuration. */
  callerClientId?: string | null;
  callerObjectId?: string | null;
  callerTenantId?: string | null;
  tokenAudience?: string | null;
  /** Empty means no app-role assignment exists for the caller. */
  callerRoles: string[];
  callerIdentityType?: string | null;
  requestedScope?: string | null;
  entitlementUrl?: string | null;
  appCode?: string | null;
  allowedTenants: AppLauncherAllowedTenant[];
  probeTenantId?: string | null;
  responseStatusCode?: number | null;
  /**
   * The keys AppLauncher's payload actually carried, when the probe got a document back. The only
   * place the real contract is observable rather than assumed — the mapping editor offers these as
   * the choices for each QuickDial limit. Null when no document was returned.
   */
  observedPayload?: AppLauncherObservedPayload | null;
  /**
   * AppLauncher's response body verbatim, for the diagnostics log. Present for rejected and error
   * responses too — those are the runs worth reading, and `observedPayload` is null for them.
   * Rendered as text only: never parsed into UI state, since it is unreviewed upstream content.
   */
  rawResponseBody?: string | null;
  /** True when the body was cut off at the API's size cap. */
  rawResponseBodyTruncated?: boolean;
  /**
   * Why a returned body failed QuickDial's contract check. Without this, a rejected 200 would read
   * as "no payload observed" and the real reason would only exist in Application Insights.
   */
  payloadRejectionReason?: string | null;
};

/**
 * What one AppLauncher entitlement document contained, reduced to what the mapping editor needs.
 * `limits` is empty by contract for a `blocked` entitlement — that tenant has nothing to map, which
 * is different from the keys being missing.
 */
export type AppLauncherObservedPayload = {
  accessMode?: string | null;
  features: Record<string, boolean>;
  limits: Record<string, number>;
  hasNoLimits: boolean;
};

export async function getPortalTenants(): Promise<PortalTenantInventoryResponse> {
  return apiFetch<PortalTenantInventoryResponse>("/portal/tenants");
}

// ---------- Usage analytics ----------

/** Usage-event kinds understood by the backend (mirrors UsageEventKinds on the API). */
export type UsageEventKind =
  | 'visit'
  | 'contact-search'
  | 'directory-search'
  | 'call'
  | 'sms'
  | 'email'
  | 'contact-open'
  | 'quickdial-view'
  | 'contactlist-view';

export type UsageEvent = {
  kind: UsageEventKind;
  /** ISO-8601 instant the event occurred; defaults to now server-side when omitted. */
  occurredAt?: string;
  count?: number;
};

/**
 * Session-level access context: where (host surface) and how (OS + form factor) the app is running.
 * Mirrors the backend `UsageSessionContext`; tokens mirror the `Usage*` enums. Facets may be omitted and
 * default to their `unknown` bucket server-side.
 */
export type UsageContext = {
  host?: string;
  os?: string;
  formFactor?: string;
};

/**
 * Ships a batch of usage events to the tenant-scoped ingest endpoint. Fire-and-forget: errors are
 * swallowed so instrumentation never disrupts the app. `context` carries the session's host/platform so
 * the backend can bucket the batch. Pass `keepalive` when flushing during page hide/unload so the request
 * survives the document being torn down (sendBeacon can't attach the bearer token, so we use fetch
 * keepalive instead).
 */
export async function recordUsageEvents(
  events: UsageEvent[],
  context?: UsageContext,
  keepalive = false,
): Promise<void> {
  if (!events.length) return;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    if (authHeaderProvider) {
      const token = await authHeaderProvider();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
  } catch {
    // Ignore token errors — the endpoint will 401 and the batch is simply dropped.
  }

  try {
    await fetch(`${API_BASE_URL}/api/usage`, {
      method: 'POST',
      headers,
      body: JSON.stringify(context ? { events, context } : { events }),
      credentials: 'include',
      keepalive,
    });
  } catch {
    // Best-effort telemetry: never surface network errors to the caller.
  }
}

export type UsageTotals = {
  visits: number;
  contactSearches: number;
  directorySearches: number;
  searches: number;
  calls: number;
  smsMessages: number;
  emails: number;
  contactOpens: number;
  quickDialViews: number;
  contactListViews: number;
};

export type UsageDailyPoint = UsageTotals & {
  day: string;
  activeUsers: number;
};

export type UsageTenantSummary = {
  tenantId: string;
  tenantName: string;
  totals: UsageTotals;
  activeUsers: number;
  entitledViewers: number;
  lists: number;
  sites: number;
  users: number;
  contacts: number;
  status: string;
  subscriptionTier: string;
  /** yyyy-MM-dd the tenant was first provisioned (empty when unknown). */
  createdDay: string;
  /** yyyy-MM-dd of the tenant's most recent content activity (empty when unknown). */
  lastActivityDay: string;
  /** Host surface with the most visits for this tenant under the active filters (empty when no context). */
  topHostSurface: string;
  /** OS with the most visits for this tenant under the active filters (empty when no context). */
  topPlatform: string;
  /** Distinct contacts/mailboxes this tenant's contact sync reached in the period. */
  syncReach: UsageSyncReach;
};

/**
 * Contact-sync reach over the selected period, deduplicated: distinct contacts written to mailboxes and
 * distinct mailboxes that received them. Not comparable to the sweep's own counters, which sum every
 * write (one contact on five mailboxes counts five times there, once here).
 */
export type UsageSyncReach = {
  uniqueContactsSynced: number;
  uniqueUsersSynced: number;
};

export type UsageTierCount = {
  tier: string;
  tenants: number;
};

/** Visit count for one value of an access dimension (host surface / OS / form factor). */
export type UsageDimensionCount = {
  key: string;
  visits: number;
};

/** One day of the host-surface time series: visits per host (sparse — only hosts with visits that day). */
export type UsageHostDailyPoint = {
  day: string;
  visits: Record<string, number>;
};

export type UsageTenantOption = {
  tenantId: string;
  tenantName: string;
};

export type UsageAppliedFilters = {
  tenantId: string | null;
  tier: string | null;
  status: string | null;
  minContacts: number | null;
  hostSurfaces: string[];
  platforms: string[];
  formFactors: string[];
};

export type UsageAnalyticsResponse = {
  fromDay: string;
  toDay: string;
  totals: UsageTotals;
  activeUsers: number;
  entitledViewers: number;
  totalLists: number;
  totalSites: number;
  totalContacts: number;
  totalUsers: number;
  tenantCount: number;
  newTenants: number;
  /** Platform-wide sync reach: the sum of per-tenant distinct counts (ids are tenant-scoped). */
  syncReach: UsageSyncReach;
  series: UsageDailyPoint[];
  tenants: UsageTenantSummary[];
  tierDistribution: UsageTierCount[];
  hostSurfaceDistribution: UsageDimensionCount[];
  platformDistribution: UsageDimensionCount[];
  formFactorDistribution: UsageDimensionCount[];
  hostSurfaceSeries: UsageHostDailyPoint[];
  availableTenants: UsageTenantOption[];
  availableTiers: string[];
  availableStatuses: string[];
  availableHostSurfaces: string[];
  availablePlatforms: string[];
  availableFormFactors: string[];
  appliedFilters: UsageAppliedFilters;
  timestamp: string;
};

/** Operator-side filters for the usage-analytics query. All are optional and combinable. The access
 *  dimensions (host/platform/form factor) are multi-select — an empty array means "no filter". */
export type UsageAnalyticsFilters = {
  fromDay?: string;
  toDay?: string;
  tenantId?: string | null;
  tier?: string | null;
  status?: string | null;
  minContacts?: number | null;
  hostSurfaces?: string[];
  platforms?: string[];
  formFactors?: string[];
};

export async function getPortalUsage(filters: UsageAnalyticsFilters = {}): Promise<UsageAnalyticsResponse> {
  const params = new URLSearchParams();
  if (filters.fromDay) params.append('from', filters.fromDay);
  if (filters.toDay) params.append('to', filters.toDay);
  if (filters.tenantId) params.append('tenantId', filters.tenantId);
  if (filters.tier) params.append('tier', filters.tier);
  if (filters.status) params.append('status', filters.status);
  if (filters.minContacts != null && filters.minContacts > 0) {
    params.append('minContacts', String(filters.minContacts));
  }
  if (filters.hostSurfaces?.length) params.append('host', filters.hostSurfaces.join(','));
  if (filters.platforms?.length) params.append('platform', filters.platforms.join(','));
  if (filters.formFactors?.length) params.append('form', filters.formFactors.join(','));
  const query = params.toString();
  return apiFetch<UsageAnalyticsResponse>(`/portal/usage${query ? `?${query}` : ''}`);
}

// ---------- Contact-sync observability (ADR 0006) ----------
export type SyncQueueStatus = {
  name: string;
  exists: boolean;
  messageCount: number;
  oldestMessageInsertedOn?: string;
  oldestMessageAgeSeconds?: number;
};

export type SyncPoisonMessage = {
  queue: string;
  tenantId?: string;
  upn?: string;
  contactId?: string;
  op?: string;
  insertedOn?: string;
  dequeueCount: number;
};

export type SyncTenantStatus = {
  tenantId: string;
  poisonCount: number;
};

export type SyncStatusResponse = {
  status: string;
  queues: SyncQueueStatus[];
  poisonMessages: SyncPoisonMessage[];
  tenantsBehind: SyncTenantStatus[];
  totalPoisonCount: number;
  timestamp: string;
};

export async function getPortalSyncStatus(): Promise<SyncStatusResponse> {
  return apiFetch<SyncStatusResponse>("/portal/sync/status");
}

export type SyncLastRun = {
  startedAt: string;
  completedAt?: string;
  status: string;
  trigger: string;
  durationMs?: number;
  error?: string;
  tenantsSyncEnabled?: number;
  usersProcessed?: number;
  usersFailed?: number;
  totalContactsSynced?: number;
  totalCreated?: number;
  totalUpdated?: number;
  totalSkipped?: number;
  totalFailed?: number;
};

export type SyncScheduleResponse = {
  /**
   * The timer tick, not the cadence. The sweep's actual frequency is `intervalMinutes`; the tick only
   * decides how precisely that interval can be observed.
   */
  cronExpression?: string;
  nextRunAt?: string;
  isRunning: boolean;
  lastRun?: SyncLastRun;
  timestamp: string;
  /** Operator-configured sweep interval in minutes. */
  intervalMinutes: number;
  /** Smallest settable interval — equals the tick granularity, so nothing below it is honourable. */
  minimumIntervalMinutes: number;
  maximumIntervalMinutes: number;
  intervalUpdatedAt?: string;
  intervalUpdatedBy?: string;
};

export async function getPortalSyncSchedule(): Promise<SyncScheduleResponse> {
  return apiFetch<SyncScheduleResponse>("/portal/sync/schedule");
}

/** Changes how often the reconciliation sweep runs. Takes effect on the next timer tick. */
export async function setPortalSyncInterval(intervalMinutes: number): Promise<SyncScheduleResponse> {
  return apiFetch<SyncScheduleResponse>("/portal/sync/schedule", {
    method: "PUT",
    body: JSON.stringify({ intervalMinutes }),
  });
}

/** One historical sweep run in the portal's run history. */
export type SyncRun = {
  id: string;
  startedAt: string;
  completedAt?: string;
  status: string;
  trigger: string;
  durationMs?: number;
  error?: string;
  tenantsSyncEnabled: number;
  usersProcessed: number;
  usersFailed: number;
  totalContactsSynced: number;
  totalCreated: number;
  totalUpdated: number;
  totalSkipped: number;
  totalFailed: number;
};

export type SyncHistoryResponse = {
  runs: SyncRun[];
  timestamp: string;
};

export async function getPortalSyncHistory(limit?: number): Promise<SyncHistoryResponse> {
  const query = limit ? `?limit=${limit}` : "";
  return apiFetch<SyncHistoryResponse>(`/portal/sync/history${query}`);
}

/**
 * One contact change that travelled the event-driven sync path. Distinct from `SyncRun`: that is
 * one reconciliation sweep across every tenant, this is one contact changing in one tenant.
 */
export type SyncEvent = {
  id: string;
  dispatchedAt: string;
  completedAt: string | null;
  status: string;
  op: string;
  tenantId: string;
  tenantName: string | null;
  contactId: string;
  contactName: string | null;
  listNames: string[];
  mailboxCount: number;
  mailboxesSucceeded: number;
  mailboxesFailed: number;
  mailboxesSkipped: number;
  error: string | null;
};

export type SyncEventsResponse = {
  events: SyncEvent[];
  timestamp: string;
};

export async function getPortalSyncEvents(limit?: number, tenantId?: string): Promise<SyncEventsResponse> {
  const params = new URLSearchParams();
  if (limit) {
    params.set("limit", String(limit));
  }
  if (tenantId) {
    params.set("tenantId", tenantId);
  }
  const query = params.toString();
  return apiFetch<SyncEventsResponse>(`/portal/sync/events${query ? `?${query}` : ""}`);
}

/** Result of asking the API to start a manual sync. `alreadyRunning` maps to a 409 conflict. */
export type RunSyncResult = { started: boolean; alreadyRunning: boolean };

export async function runPortalSyncNow(): Promise<RunSyncResult> {
  try {
    await apiFetch<unknown>("/portal/sync/run", { method: "POST" }, undefined, true);
    return { started: true, alreadyRunning: false };
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      return { started: false, alreadyRunning: true };
    }
    throw error;
  }
}

/** Outcome of re-driving or discarding a contact-sync poison queue (ADR 0006). */
export type PoisonActionResult = {
  queue: string;
  action: string;
  processed: number;
  timestamp: string;
};

/**
 * Re-drives a poison queue's messages back onto its live queue, or discards them. `action` is
 * "redrive" (reprocess after the cause is fixed) or "discard" (drop unrecoverable messages).
 */
export async function runPortalPoisonAction(
  action: "redrive" | "discard",
  queue: string,
): Promise<PoisonActionResult> {
  return apiFetch<PoisonActionResult>(`/portal/sync/poison/${action}`, {
    method: "POST",
    body: JSON.stringify({ queue }),
  });
}

/** Outcome of collapsing duplicate user documents (ADR 0006). */
export type DeduplicateUsersResult = {
  tenantsScanned: number;
  duplicateUsersFound: number;
  documentsDeleted: number;
  timestamp: string;
};

/**
 * Collapses duplicate user documents. Omit `tenantId` to scan every tenant; pass one to scope it.
 * Keeps the most recently updated document per userId and deletes the rest.
 */
export async function deduplicatePortalUsers(tenantId?: string): Promise<DeduplicateUsersResult> {
  return apiFetch<DeduplicateUsersResult>("/portal/users/deduplicate", {
    method: "POST",
    body: JSON.stringify(tenantId ? { tenantId } : {}),
  });
}

/** One Outlook contact row as the mailbox diagnostics report it (mirrors MailboxDiagnosticsContact). */
export type MailboxDiagnosticsContact = {
  outlookId: string;
  folderId: string | null;
  folderName: string;
  displayName: string | null;
  emailAddresses: string[];
  categories: string[];
  createdDateTime: string | null;
  lastModifiedDateTime: string | null;
  quickDialId: string | null;
  markerSource: 'extendedProperty' | 'notes' | 'none';
  hasContentHash: boolean;
  hasManualSaveStamp: boolean;
  quickDialContactState: 'active' | 'deleted' | 'missing' | null;
};

export type MailboxDuplicateGroup = {
  kind: 'sameQuickDialId' | 'sameEmail';
  key: string;
  displayName: string | null;
  pattern: 'differentFolders' | 'createdTogether' | 'createdOverTime';
  createdSpreadSeconds: number | null;
  copies: MailboxDiagnosticsContact[];
};

export type MailboxDiagnosticsFolder = {
  id: string | null;
  displayName: string;
  isDefault: boolean;
  isQuickDialFolder: boolean;
  contactCount: number;
  quickDialContactCount: number;
};

export type MailboxDiagnosticsResponse = {
  tenantId: string;
  userPrincipalName: string;
  quickDialFolderName: string;
  summary: {
    foldersInspected: number;
    quickDialFolderCount: number;
    quickDialFolderContacts: number;
    distinctQuickDialIds: number;
    extraCopies: number;
    sameEmailGroups: number;
    unmarkedInQuickDialFolder: number;
    quickDialContactsOutsideFolder: number;
  };
  folders: MailboxDiagnosticsFolder[];
  duplicateGroups: MailboxDuplicateGroup[];
  unmarkedInQuickDialFolder: MailboxDiagnosticsContact[];
  quickDialContactsOutsideFolder: MailboxDiagnosticsContact[];
  inspectedAt: string;
};

/** Read-only inspection of one user's Outlook contacts, to explain duplicate rows. Audited per call. */
export async function getPortalMailboxDiagnostics(tenantId: string, userPrincipalName: string): Promise<MailboxDiagnosticsResponse> {
  const params = new URLSearchParams({ tenantId, upn: userPrincipalName });
  return apiFetch<MailboxDiagnosticsResponse>(`/portal/sync/mailbox?${params.toString()}`);
}

/** One dynamic (Entra-directory) contact list's sync state, for the portal monitoring table. */
export type DynamicListStatus = {
  tenantId: string;
  listId: string;
  name: string;
  dynamicQuery?: string;
  contactCount: number;
  lastSyncStatus?: string;
  lastSyncAt?: string;
  lastSyncError?: string;
  lastSyncContactCount?: number;
};

export type DynamicListSyncStatusResponse = {
  status: string;
  totalTenants: number;
  totalDynamicLists: number;
  totalContacts: number;
  listsWithErrors: number;
  listsNeverSynced: number;
  cronExpression?: string;
  nextRunAt?: string;
  isRunning: boolean;
  lists: DynamicListStatus[];
  timestamp: string;
};

export async function getPortalDynamicListSyncStatus(): Promise<DynamicListSyncStatusResponse> {
  return apiFetch<DynamicListSyncStatusResponse>("/portal/dynamic-lists/status");
}

/** Starts a manual dynamic-list reconciliation sweep. `alreadyRunning` maps to a 409 conflict. */
export async function runPortalDynamicListSyncNow(): Promise<RunSyncResult> {
  try {
    await apiFetch<unknown>("/portal/dynamic-lists/run", { method: "POST" }, undefined, true);
    return { started: true, alreadyRunning: false };
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      return { started: false, alreadyRunning: true };
    }
    throw error;
  }
}

export async function getPortalTenant(tenantId: string): Promise<PortalTenantDetail> {
  return apiFetch<PortalTenantDetail>(`/portal/tenants/${encodeURIComponent(tenantId)}`);
}

export async function updatePortalTenantLicense(tenantId: string, payload: UpdateTenantLicensePayload): Promise<TenantLicenseConfiguration> {
  return apiFetch<TenantLicenseConfiguration>(`/portal/tenants/${encodeURIComponent(tenantId)}/license`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function getPlatformLicensingSettings(): Promise<PlatformLicensingSettings> {
  return apiFetch<PlatformLicensingSettings>('/portal/settings/licensing');
}

/**
 * The calling tenant admin's licensing overview. Tenant-scoped and safe for the Admin page, unlike
 * the `/portal/settings/licensing` endpoint which requires hosting-admin rights.
 */
export async function getTenantLicensingOverview(): Promise<TenantLicensingOverview> {
  return apiFetch<TenantLicensingOverview>('/tenants/licensing');
}

/**
 * The same overview, resolved without the API's cached entitlement.
 *
 * For the moment a tenant admin comes back from AppLauncher having just bought, changed or cancelled
 * a subscription: purchasing happens in its own tab, so this one is still holding the answer from
 * before it, and the cached entitlement behind that answer would otherwise keep saying "inactive"
 * until its TTL runs out.
 *
 * A POST because it spends an upstream call — it must not be prefetched or replayed — though it
 * changes nothing and enforces exactly the authorization the read does. The API throttles the
 * underlying refresh per tenant, so calling this on a user gesture is safe.
 */
export async function refreshTenantLicensingOverview(): Promise<TenantLicensingOverview> {
  return apiFetch<TenantLicensingOverview>('/tenants/licensing/refresh', { method: 'POST' });
}

/**
 * The pre-flip reconciliation dry run. Read-only, but it fans out one upstream call per tenant, so it
 * is requested on demand rather than with the settings page.
 */
export async function getPlatformLicensingReconciliation(): Promise<LicensingReconciliationReport> {
  return apiFetch<LicensingReconciliationReport>('/portal/settings/licensing/reconciliation');
}

/**
 * Runs the live AppLauncher self-test and returns the coordinates to register on the AppLauncher
 * side. Read-only in both systems; `tenantId` selects the tenant the entitlement probe asks for.
 */
export async function getAppLauncherConnectivity(tenantId?: string): Promise<AppLauncherConnectivityReport> {
  const query = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
  return apiFetch<AppLauncherConnectivityReport>(`/portal/settings/licensing/connectivity${query}`);
}

/** Switches the platform licensing mode. Affects every tenant at once and is audited server-side. */
export async function updatePlatformLicensingMode(mode: LicensingProvider): Promise<PlatformLicensingSettings> {
  return apiFetch<PlatformLicensingSettings>('/portal/settings/licensing', {
    method: 'PUT',
    body: JSON.stringify({ mode }),
  });
}

/**
 * Stores the AppLauncher entitlement key mapping, or resets it to the deployment-configured keys when
 * `mapping` is null. Rejected server-side if a key is blank or two limits share one key, since an
 * invalid mapping resolves as misconfigured and blocks writes for every tenant.
 */
export async function updatePlatformLicensingMapping(
  mapping: Omit<PlatformLicensingMapping, 'isOverridden'> | null,
): Promise<PlatformLicensingSettings> {
  return apiFetch<PlatformLicensingSettings>('/portal/settings/licensing/mapping', {
    method: 'PUT',
    body: JSON.stringify({ mapping }),
  });
}

/**
 * The help-centre page each tenant role opens. A null role uses QuickDial's default help space; a value
 * may carry `{lang}` for the reader's help language.
 */
export interface HelpCenterUrls {
  subscriber: string | null;
  manager: string | null;
  tenantAdmin: string | null;
}

export interface PlatformHelpCenterSettings {
  urls: HelpCenterUrls;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** The per-role help URLs, readable by any signed-in user. */
export async function getHelpCenterUrls(): Promise<HelpCenterUrls> {
  return apiFetch<HelpCenterUrls>('/help/urls');
}

export async function getPlatformHelpCenterSettings(): Promise<PlatformHelpCenterSettings> {
  return apiFetch<PlatformHelpCenterSettings>('/portal/settings/help');
}

/** Stores the per-role help URLs for every tenant. A blank role goes back to the default help space. */
export async function updatePlatformHelpCenterSettings(urls: HelpCenterUrls): Promise<PlatformHelpCenterSettings> {
  return apiFetch<PlatformHelpCenterSettings>('/portal/settings/help', {
    method: 'PUT',
    body: JSON.stringify(urls),
  });
}

export async function deletePortalTenant(tenantId: string): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authHeaderProvider) {
    try {
      const token = await authHeaderProvider();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      // Let the API return an auth error if token acquisition fails.
    }
  }

  const url = `${API_BASE_URL}/api/portal/tenants/${encodeURIComponent(tenantId)}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let errorMessage = `API error ${response.status}`;
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      errorMessage = `API error ${response.status}: ${response.statusText}`;
    }

    throw new Error(errorMessage);
  }
}

// ---------- Start page ----------

/**
 * The public start page for the landing page. Anonymous and called before sign-in, so it skips
 * `apiFetch`, which would wait for and try to use a token provider.
 */
export async function getStartPage(signal?: AbortSignal): Promise<PublicStartPage> {
  const response = await fetch(`${API_BASE_URL}/api/start-page`, { signal: signal ?? null });
  if (!response.ok) {
    throw new ApiError(response.status, `API error ${response.status}`);
  }

  return response.json() as Promise<PublicStartPage>;
}

/** Where an uploaded start-page image is served from. Anonymous, so it works in a plain `<img>`. */
export function startPageImageUrl(imageId: string): string {
  return `${API_BASE_URL}/api/start-page/images/${encodeURIComponent(imageId)}`;
}

export async function getPlatformStartPage(): Promise<PlatformStartPage> {
  return apiFetch<PlatformStartPage>('/portal/start-page');
}

/**
 * Saves the start page layout. `blocks: null` resets it to the built-in default. `expectedUpdatedAt` is the
 * version the editor loaded; the API answers 409 when someone else saved in between.
 */
export async function updatePlatformStartPage(
  blocks: StartPageBlock[] | null,
  expectedUpdatedAt: string | null,
): Promise<PlatformStartPage> {
  return apiFetch<PlatformStartPage>('/portal/start-page', {
    method: 'PUT',
    body: JSON.stringify({ blocks, expectedUpdatedAt }),
  });
}

/** Uploads a PNG, JPEG, GIF or WebP image (at most 1 MB) as the raw request body. */
export async function uploadStartPageImage(file: Blob, fileName: string): Promise<StartPageImageUpload> {
  // A raw body, which `apiFetch` cannot send: it logs and sets every body as JSON.
  const headers: Record<string, string> = { 'Content-Type': file.type || 'application/octet-stream' };
  if (authHeaderProvider) {
    try {
      const token = await authHeaderProvider();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      // Let the API return an auth error if token acquisition fails.
    }
  }

  const url = `${API_BASE_URL}/api/portal/start-page/images?fileName=${encodeURIComponent(fileName)}`;
  const response = await fetch(url, { method: 'POST', headers, body: file, credentials: 'include' });

  if (!response.ok) {
    let errorMessage = `API error ${response.status}`;
    try {
      const errorData = await response.json();
      if (errorData.error) errorMessage = errorData.error;
    } catch {
      // Not JSON; keep the status message.
    }
    throw new ApiError(response.status, errorMessage);
  }

  return response.json() as Promise<StartPageImageUpload>;
}

// ---------- Settings ----------
export type TenantSettings = {
  id: string;
  tenantId: string;
  type: "settings";
  displayName?: string;
  allowSelfSubscribe?: boolean;
  siteAwareDefault?: boolean;
  enableContactSync?: boolean;
  contactSyncSuffix?: string;
  useTeamsCalling?: boolean;
  quickDialAsStartPage?: boolean;
  showTeamsPresence?: boolean;
  outlookSyncEnabled?: boolean;
  showProfilePhotosInLists?: boolean;
  /**
   * When true, only tenant admins may add or remove subscribers on contact lists.
   * Self-subscribe options are also disabled for non-admins.
   * Defaults to false (open subscriber management for list managers).
   */
  controlledSubscribers?: boolean;
  /**
   * Tenant-wide default contact-list (table) column layout: ordered column keys
   * (built-in field keys or "custom:<fieldId>"). Empty/undefined means the
   * built-in default layout. Used as the fallback when a user has not chosen
   * their own column layout for a list.
   */
  defaultListColumns?: string[];
  /**
   * The single Outlook category QuickDial writes on every contact it syncs, whatever wrote it.
   * Undefined or blank means the "QuickDial" default.
   */
  contactCategoryPrefix?: string;
  /**
   * When true (the default), each contact list a contact belongs to is written as its own
   * Outlook category.
   */
  contactCategoryIncludeListNames?: boolean;
  /**
   * When false, QuickDial stops writing its own category, leaving only the list-name categories.
   * Defaults to true. The field name is historical — it once toggled a pair of "- Auto Sync" /
   * "- Manual Save" categories, which have collapsed into the single `contactCategoryPrefix` one.
   */
  contactCategoryIncludeSyncState?: boolean;
  /**
   * When false, organization directory search is turned off for the tenant even when the
   * subscription includes it. Undefined means on; changeable by tenant admins only.
   */
  directorySearchEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
};

export async function getTenantSettings(): Promise<TenantSettings> {
  // Suppress 403 logging - non-admin users will always get 403
  return apiFetch<TenantSettings>("/settings", {}, [403]);
}

export async function updateTenantSettings(settings: Partial<TenantSettings>): Promise<TenantSettings> {
  return apiFetch<TenantSettings>("/settings", {
    method: "PATCH",
    body: JSON.stringify(settings)
  });
}

/**
 * What deleting the tenant would destroy, counted per resource. Drives the confirmation screen so the
 * admin sees the damage before agreeing to it; the server recounts before it actually deletes.
 */
export type TenantDeletionPreview = {
  tenantId: string;
  organizationName: string | null;
  /** The exact text the admin has to type back before the delete button becomes usable. */
  confirmationPhrase: string;
  contactCount: number;
  /** Already soft-deleted contacts: still stored, still destroyed, invisible in the app today. */
  deletedContactCount: number;
  listCount: number;
  siteCount: number;
  userCount: number;
  groupSubscriberCount: number;
  labelCount: number;
  orderCount: number;
  auditLogCount: number;
  /** Settings, licensing and sync telemetry: real records, just not itemised individually. */
  otherDocumentCount: number;
  totalDocumentCount: number;
  /** Mailboxes receiving synced contacts. Those live in Microsoft 365 and are NOT removed. */
  syncedMailboxCount: number;
};

export type TenantDeletionResult = {
  tenantId: string;
  organizationName: string | null;
  deletedDocumentCount: number;
  deletedAt: string;
};

export async function getTenantDeletionPreview(): Promise<TenantDeletionPreview> {
  return apiFetch<TenantDeletionPreview>("/tenants/deletion-preview");
}

/**
 * Irreversibly deletes the organization of the signed-in admin. There is no tenant id to pass: the
 * server only ever acts on the tenant in the caller token. `confirmation` must match the preview
 * confirmationPhrase, otherwise the call is rejected with 400.
 */
export async function deleteOwnTenant(confirmation: string): Promise<TenantDeletionResult> {
  return apiFetch<TenantDeletionResult>("/tenants/delete", {
    method: "POST",
    body: JSON.stringify({ confirmation })
  });
}

export async function getTenantConfiguration(): Promise<TenantConfiguration> {
  return apiFetch<TenantConfiguration>("/tenants/config");
}

export async function updateBrandColors(brandColors: string[]): Promise<TenantConfiguration> {
  return apiFetch<TenantConfiguration>("/tenants/config/brand-colors", {
    method: "PATCH",
    body: JSON.stringify({ brandColors })
  });
}

// ---------- Access governance (effective tenant admins only) ----------
export interface AccessGovernanceState {
  mode: AccessGovernanceMode;
  governanceRevision: number;
  storedTenantAdminCount: number;
}

export interface AccessGovernanceChangeResult {
  previousMode: AccessGovernanceMode;
  mode: AccessGovernanceMode;
  governanceRevision: number;
  actorAdminSource: Exclude<TenantAdminSource, 'none'>;
}

/** A row from GET /management/users: a stored tenant user, or a group subscriber (isGroup). */
export interface ManagementUserRow {
  id: string;
  userId: string;
  upn?: string | null;
  name?: string | null;
  roles: string[];
  /** Stored role only; says nothing about the user's Entra QuickDial.Admin assignment. */
  isStoredTenantAdmin?: boolean;
  isGroup?: boolean;
}

export async function getManagementUsers(): Promise<ManagementUserRow[]> {
  return apiFetch<ManagementUserRow[]>("/management/users");
}

export async function getAccessGovernance(): Promise<AccessGovernanceState> {
  return apiFetch<AccessGovernanceState>("/tenants/config/access-governance");
}

// Server-authoritative: the transition is only real once this resolves. Errors (400 invalid/bootstrap
// required, 403 not permitted for this caller, 409 concurrent change) surface as ApiError.
export async function updateAccessGovernance(
  mode: AccessGovernanceMode,
  bootstrapAdminUserId?: string
): Promise<AccessGovernanceChangeResult> {
  return apiFetch<AccessGovernanceChangeResult>("/tenants/config/access-governance", {
    method: "PATCH",
    body: JSON.stringify(bootstrapAdminUserId ? { mode, bootstrapAdminUserId } : { mode })
  });
}

// ---------- Access governance, HostingPortal view + override (ADR 0009 §7) ----------
export interface PortalAccessGovernanceUser {
  userId: string;
  name?: string | null;
  upn?: string | null;
  isStoredTenantAdmin: boolean;
}

export interface PortalAccessGovernanceView {
  tenantId: string;
  mode: AccessGovernanceMode;
  /** False when the tenant has no configuration yet; the mode then reads as entraOnly and cannot be changed. */
  isProvisioned: boolean;
  governanceRevision: number;
  storedTenantAdminCount: number;
  users: PortalAccessGovernanceUser[];
}

export interface PortalAccessGovernanceOverrideResult {
  previousMode: AccessGovernanceMode;
  mode: AccessGovernanceMode;
  governanceRevision: number;
}

export async function getPortalTenantAccessGovernance(tenantId: string): Promise<PortalAccessGovernanceView> {
  return apiFetch<PortalAccessGovernanceView>(`/portal/tenants/${encodeURIComponent(tenantId)}/access-governance`);
}

// Hosting override: bypasses the customer transition matrix but keeps the appManaged bootstrap-admin
// invariant, the ETag batch and the tenant audit entry. The reason is mandatory and audited.
export async function overridePortalTenantAccessGovernance(
  tenantId: string,
  payload: { mode: AccessGovernanceMode; reason: string; bootstrapAdminUserId?: string }
): Promise<PortalAccessGovernanceOverrideResult> {
  return apiFetch<PortalAccessGovernanceOverrideResult>(`/portal/tenants/${encodeURIComponent(tenantId)}/access-governance`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

// Readable by any authenticated tenant member so contact editors can render custom fields.
export async function getCustomContactFields(): Promise<CustomFieldDefinition[]> {
  const response = await apiFetch<{ customContactFields: CustomFieldDefinition[] }>(
    "/tenants/config/custom-contact-fields"
  );
  return response.customContactFields ?? [];
}

// Admin-only: replaces the tenant's custom contact field definitions.
export async function updateCustomContactFields(
  customContactFields: CustomFieldDefinition[]
): Promise<TenantConfiguration> {
  return apiFetch<TenantConfiguration>("/tenants/config/custom-contact-fields", {
    method: "PATCH",
    body: JSON.stringify({ customContactFields })
  });
}

export async function testContactSync(targetUserId: string, testContactData?: any): Promise<{
  success: boolean;
  message: string;
  testContact: any;
  syncSuffix: string;
  targetUser: string;
}> {
  return apiFetch<{
    success: boolean;
    message: string;
    testContact: any;
    syncSuffix: string;
    targetUser: string;
  }>("/settings/test-contact-sync", {
    method: "POST",
    body: JSON.stringify({ targetUserId, testContactData })
  });
}

// ---------- Contact Export to Outlook ----------
export async function saveContactToOutlook(contact: Contact): Promise<{
  success: boolean;
  message: string;
  contactId?: string;
}> {
  // Save individual contact directly to Outlook via Graph API
  return apiFetch<{
    success: boolean;
    message: string;
    contactId?: string;
  }>("/sync-to-outlook/trigger-user-sync", {
    method: "POST",
    body: JSON.stringify({
      contact: {
        id: contact.id,
        firstName: contact.firstName,
        lastName: contact.lastName,
        title: contact.title,
        department: contact.department,
        company: contact.company,
        email: contact.email,
        phone: contact.phone,
        mobile: contact.mobile,
        location: contact.location,
        street: contact.street,
        city: contact.city,
        postalCode: contact.postalCode,
        country: contact.country,
        notes: contact.notes
      }
    })
  });
}

export async function syncContactsToOutlook(contacts: Contact[]): Promise<{
  totalContacts: number;
  successful: number;
  failed: number;
  skipped: number;
  message: string;
}> {
  // Save multiple contacts directly to Outlook via Graph API
  return apiFetch<{
    totalContacts: number;
    successful: number;
    failed: number;
    skipped: number;
    message: string;
  }>("/sync-to-outlook/trigger-user-sync", {
    method: "POST",
    body: JSON.stringify({
      contactIds: contacts.map(c => c.id)
    })
  });
}

// ---------- Dynamic List Validation & Sync ----------
export async function validateDynamicQuery(query: string): Promise<{
  valid: boolean;
  error?: string;
  sampleUsers?: Array<{
    id: string;
    displayName: string;
    mail: string;
    jobTitle?: string;
    department?: string;
  }>;
  estimatedCount?: number;
}> {
  // Directory reads run server-side with the Contact Sync app's admin-consented
  // User.Read.All permission. The browser sign-in token only holds delegated
  // User.Read and cannot read other users' attributes, so no Graph token is sent.
  return apiFetch<{
    valid: boolean;
    error?: string;
    sampleUsers?: Array<{
      id: string;
      displayName: string;
      mail: string;
      jobTitle?: string;
      department?: string;
    }>;
    estimatedCount?: number;
  }>("/lists/validate-dynamic-query", {
    method: "POST",
    body: JSON.stringify({ dynamicQuery: query })
  });
}

export async function syncDynamicList(listId: string): Promise<{
  success: boolean;
  contactsFound: number;
  contactsCreated: number;
  contactsUpdated: number;
  contactsRemoved: number;
}> {
  // Directory reads run server-side with the Contact Sync app's admin-consented
  // permissions; no browser Graph token is required.
  return apiFetch<{
    success: boolean;
    contactsFound: number;
    contactsCreated: number;
    contactsUpdated: number;
    contactsRemoved: number;
  }>(`/lists/${listId}/sync`, {
    method: "POST"
  });
}

// =========================================
// Admin APIs for deleted contact management
// =========================================

export async function getDeletedContacts(searchQuery?: string): Promise<Contact[]> {
  const params = new URLSearchParams({ deleted: 'true' });
  if (searchQuery) {
    params.append('q', searchQuery);
  }
  return apiFetch<Contact[]>(`/portal/contacts?${params.toString()}`);
}

export async function restoreContact(contactId: string, listId: string): Promise<Contact> {
  return apiFetch<Contact>(`/portal/contacts/${contactId}/restore`, {
    method: 'POST',
    body: JSON.stringify({ listIds: [listId] })
  });
}

export async function permanentlyDeleteContact(contactId: string): Promise<{ ok: boolean; message: string }> {
  return apiFetch<{ ok: boolean; message: string }>(`/portal/contacts/${contactId}`, {
    method: 'DELETE'
  });
}

// User search (for sharing contacts in Teams)
export type UserSearchResult = {
  id: string;
  displayName: string;
  email: string;
  jobTitle?: string;
  department?: string;
  company?: string;
};

export async function searchUsers(query: string): Promise<UserSearchResult[]> {
  const response = await apiFetch<{ value: UserSearchResult[] }>(`/users-search/search?q=${encodeURIComponent(query)}`);
  return response.value;
}

// Group search (server-side, app-only Graph with Group.Read.All) for adding group subscribers.
export type GroupSearchResult = {
  id: string;
  displayName: string;
  mail?: string | null;
  description?: string | null;
};

export async function searchGroups(query: string): Promise<GroupSearchResult[]> {
  const response = await apiFetch<{ value: GroupSearchResult[] }>(`/groups-search/search?q=${encodeURIComponent(query)}`);
  return response.value;
}

/** QuickDial's names for premium features, as `/api/me` reports them in `productFeatures`. */
export const PRODUCT_FEATURES = {
  directorySearch: 'directorySearch',
} as const;

export function hasProductFeature(me: Pick<Me, 'productFeatures'> | null | undefined, feature: string): boolean {
  return me?.productFeatures?.includes(feature) ?? false;
}

// Organization directory search (premium): people from the caller's own Entra directory, with the
// numbers needed to reach them.
export type DirectoryPerson = {
  id: string;
  displayName?: string | null;
  mail?: string | null;
  userPrincipalName?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  company?: string | null;
  officeLocation?: string | null;
  mobilePhone?: string | null;
  businessPhones: string[];
};

export type DirectorySearchResponse = {
  people: DirectoryPerson[];
};

/** The API answers 403 `feature_not_licensed` when the subscription does not include the feature. */
export function isFeatureNotLicensedError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && error.message === 'feature_not_licensed';
}

/** The API answers 403 `feature_disabled` when a tenant admin has turned the feature off. */
export function isFeatureDisabledError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && error.message === 'feature_disabled';
}

export async function searchDirectory(query: string, signal?: AbortSignal): Promise<DirectorySearchResponse> {
  return apiFetch<DirectorySearchResponse>(`/directory/search?q=${encodeURIComponent(query)}`, signal ? { signal } : {}, [403]);
}

// ---------- Integrations / Admin Consent ----------

export type IntegrationName = 'onboarding' | 'presence' | 'contact-sync';

export type AppRegistrations = {
  onboardingClientId: string | null;
  teamsPresenceClientId: string | null;
  contactSyncClientId: string | null;
  requiredPermissions: {
    onboarding: string[];
    presence: string[];
    contactSync: string[];
  };
};

// Public config endpoint (anonymous): client ids + canonical required permissions per app reg.
export async function getAppRegistrations(): Promise<AppRegistrations> {
  return apiFetch<AppRegistrations>(`/app-config/app-registrations`);
}

export type IntegrationTestResult = {
  success: boolean;
  message: string;
  clientId?: string | null;
  requiredPermissions: string[];
  details?: string | null;
};

// Each test validates ONLY its own app registration server-side.
export async function testIntegration(integration: IntegrationName, upn?: string): Promise<IntegrationTestResult> {
  const query = upn ? `?upn=${encodeURIComponent(upn)}` : '';
  return apiFetch<IntegrationTestResult>(`/integrations/test/${integration}${query}`);
}

export type ConsentStatus = 'started' | 'success' | 'failed';

export type ConsentLogEntry = {
  id: string;
  integration: string;
  status: ConsentStatus;
  clientId?: string | null;
  scopes: string[];
  actor?: string | null;
  actorUpn?: string | null;
  correlationId?: string | null;
  failureReason?: string | null;
  timestamp: string;
};

export type ConsentLogInput = {
  integration: IntegrationName;
  clientId?: string | undefined;
  scopes?: string[] | undefined;
  status: ConsentStatus;
  correlationId?: string | undefined;
  failureReason?: string | undefined;
  tenantName?: string | undefined;
};

// Record a consent attempt (tenant-admin only).
export async function logConsent(input: ConsentLogInput): Promise<ConsentLogEntry> {
  return apiFetch<ConsentLogEntry>(`/consent/log`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// Fetch consent history for an integration (tenant-admin only).
export async function getConsentHistory(integration?: IntegrationName): Promise<{ latest: ConsentLogEntry | null; entries: ConsentLogEntry[] }> {
  const query = integration ? `?integration=${encodeURIComponent(integration)}` : '';
  return apiFetch<{ latest: ConsentLogEntry | null; entries: ConsentLogEntry[] }>(`/consent/history${query}`);
}

// Audit log types for tenant license changes
export type AuditLogEntry = {
  id: string;
  category: string;
  action: string;
  status: 'started' | 'success' | 'failed';
  actorObjectId?: string | null;
  actorUpn?: string | null;
  actorDisplayName?: string | null;
  tenantName?: string | null;
  createdAt: string;
  details?: Record<string, string>;
};

export type TenantAuditLogsResponse = {
  tenantId: string;
  count: number;
  entries: AuditLogEntry[];
};

// Fetch audit logs for a tenant (portal admin only).
export async function getTenantAuditLogs(
  tenantId: string,
  fromDate?: Date,
  toDate?: Date,
  limit: number = 100
): Promise<TenantAuditLogsResponse> {
  const params = new URLSearchParams();
  params.set('category', 'tenant-license-change');
  if (fromDate) {
    params.set('fromDate', fromDate.toISOString());
  }
  if (toDate) {
    params.set('toDate', toDate.toISOString());
  }
  params.set('limit', limit.toString());

  return apiFetch<TenantAuditLogsResponse>(`/portal/tenants/${encodeURIComponent(tenantId)}/audit-logs?${params.toString()}`);
}
